import React, { useState } from 'react';
import { Heart, ArrowLeftRight, Layers } from 'lucide-react';
import { isGroupedCard, getGroupLabel } from '../utils/pieceGrouping';
import { useWallet } from '../contexts/WalletContext';

const UnifiedPieceCard = ({ 
  item, 
  onClick, 
  onBuy, 
  onTrade, 
  onFavorite, 
  isFavorited = false,
  showBuyButton = true,
  showTradeButton = true,
  showFavoriteButton = true,
  showEditionBadges = true,
  buyButtonText = "Buy now",
  tradeButtonText = "Trade",
  solPrice = 195.84,
  className = ""
}) => {
  const [imageError, setImageError] = useState(false);
  const { publicKey } = useWallet();

  // Check if this is a grouped card (frontend grouping)
  const isGrouped = isGroupedCard(item);
  const groupLabel = isGrouped ? getGroupLabel(item) : null;
  
  // Debug: Log grouping detection for pieces with edition info
  if (item.is_master === true && item.total_quantity > 1) {
    console.log('🏷️ Badge logic for piece:', item.name, {
      isGrouped,
      groupLabel,
      _isGroup: item._isGroup,
      _groupSize: item._groupSize,
      is_master: item.is_master,
      total_quantity: item.total_quantity
    });
  }

  // Helper function to get edition info
  const getEditionInfo = () => {
    const isEdition = item.is_master === true && item.total_quantity > 1;
    const totalEditions = item.total_quantity || 1;
    const availableCount = item.available_count || item.availableCount || 0;
    const soldCount = item.sold_count || item.soldCount || 0;
    
    return {
      isEdition,
      totalEditions,
      availableCount,
      soldCount,
      isSoldOut: availableCount === 0 && soldCount > 0,
      label: isEdition ? `Edition of ${totalEditions}` : '1/1'
    };
  };

  const editionInfo = getEditionInfo();

  // Smart price display logic
  const getDisplayPrice = () => {
    const isOwnedByCurrentUser = publicKey && item.holder_wallet === publicKey.toString();
    const isListed = item.listing_status === 'listed' || item.listingStatus === 'listed';
    
    // If user owns this piece
    if (isOwnedByCurrentUser) {
      // If they've listed it, show THEIR listing price
      if (isListed && (item.listing_price || item.listingPrice)) {
        return item.listing_price || item.listingPrice;
      }
      
      // If not listed, show purchase price
      if (!isListed && item.last_sale_price_lamports) {
        const priceSOL = item.last_sale_price_lamports / 1_000_000_000;
        return priceSOL;
      }
    }
    
    // For pieces not owned by user, show lowest available price
    if (item.lowestPrice !== undefined && item.lowestPrice !== null) {
      return item.lowestPrice;
    }
    return item.listing_price || item.price;
  };

  const displayPrice = getDisplayPrice();

  // Handle undefined item
  if (!item) {
    return null;
  }

  // Get thumbnail URL - prioritize different sources
  const getThumbnailUrl = () => {
    if (imageError) return '/images/piece-placeholder.png';
    
    // Check for thumbnail in different possible locations
    if (item.thumbnailUrl) {
      console.log('🖼️ Using item.thumbnailUrl:', item.thumbnailUrl);
      return item.thumbnailUrl;
    }
    if (item.thumbnail?.dataUrl) {
      console.log('🖼️ Using item.thumbnail.dataUrl:', item.thumbnail.dataUrl);
      return item.thumbnail.dataUrl;
    }
    if (item.modelFile?.dataUrl) {
      // If modelFile.dataUrl is an image (not a 3D model), use it as thumbnail
      if (item.modelFile.dataUrl.startsWith('data:image/')) {
        console.log('🖼️ Using item.modelFile.dataUrl (image)');
        return item.modelFile.dataUrl;
      }
    }
    if (item.image) {
      console.log('🖼️ Using item.image:', item.image);
      return item.image;
    }
    if (item.previewUrl && !item.previewUrl.includes('.glb') && !item.previewUrl.includes('.gltf')) {
      console.log('🖼️ Using item.previewUrl:', item.previewUrl);
      return item.previewUrl;
    }
    
    // For pieces with 3D models, try to get a thumbnail from the model file
    if (item.modelFile?.thumbnail) {
      console.log('🖼️ Using item.modelFile.thumbnail');
      return item.modelFile.thumbnail;
    }
    if (item.modelFile?.preview) {
      console.log('🖼️ Using item.modelFile.preview');
      return item.modelFile.preview;
    }
    
    console.log('🖼️ No thumbnail found, using placeholder. Item data:', item);
    return '/images/piece-placeholder.png';
  };

  const thumbnailUrl = getThumbnailUrl();

  // Check if this piece has a 3D model
  const has3DModel = () => {
    return item.modelUrl || 
           item.dataUrl || 
           item.preview_glb_url || 
           item.model_url || 
           item.previewUrl ||
           (item.modelFile && item.modelFile.dataUrl && item.modelFile.dataUrl.includes('.glb')) ||
           (item.dataUrl && (item.dataUrl.includes('.glb') || item.dataUrl.includes('.gltf') || item.dataUrl.includes('data:model/')));
  };

  const get3DModelUrl = () => {
    if (item.modelUrl) return item.modelUrl;
    if (item.dataUrl) {
      // Handle base64 data URLs
      if (item.dataUrl.includes('data:model/') || item.dataUrl.includes('data:application/')) {
        return item.dataUrl;
      } else if (item.dataUrl.includes(',')) {
        // Already formatted base64 data URL
        return item.dataUrl;
      } else if (item.dataUrl.includes('.glb') || item.dataUrl.includes('.gltf')) {
        // Handle file URLs
        return item.dataUrl;
      } else {
        // Assume it's base64 data without the data URL prefix
        return `data:${item.type || 'model/gltf-binary'};base64,${item.dataUrl}`;
      }
    }
    if (item.preview_glb_url) return item.preview_glb_url;
    if (item.model_url) return item.model_url;
    if (item.previewUrl && (item.previewUrl.includes('.glb') || item.previewUrl.includes('.gltf'))) {
      return item.previewUrl;
    }
    if (item.modelFile && item.modelFile.dataUrl && item.modelFile.dataUrl.includes('.glb')) {
      return item.modelFile.dataUrl;
    }
    return null;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(item);
    }
  };

  const handleBuy = (e) => {
    e.stopPropagation();
    if (onBuy) {
      onBuy(item);
    }
  };

  const handleTrade = (e) => {
    e.stopPropagation();
    if (onTrade) {
      onTrade(item);
    }
  };

  const handleFavorite = (e) => {
    e.stopPropagation();
    if (onFavorite) {
      onFavorite(item);
    }
  };

  const formatPrice = (price) => {
    if (typeof price === 'string' && price.includes('SOL')) {
      return price;
    }
    const numPrice = parseFloat(price) || 0;
    // Format with up to 4 decimal places, removing trailing zeros
    const formatted = numPrice.toFixed(4).replace(/\.?0+$/, '');
    return `${formatted} SOL`;
  };

  return (
    <div 
      onClick={handleClick}
      className={`cursor-pointer group relative bg-[#1a1a1a] border border-white/30 rounded-xl overflow-hidden hover:border-white transition-all duration-300 hover:shadow-xl hover:shadow-white/20 flex flex-col ${className}`}
    >
      {/* Thumbnail/3D Model Display */}
      <div className="aspect-square bg-[#2a2a2a] flex flex-col items-center justify-center relative group-hover:bg-[#3a3a3a] transition-colors duration-200 overflow-hidden">
        {/* Show thumbnail only on cards - 3D model will be in the modal */}
        <img 
          src={thumbnailUrl} 
          alt={item.name}
          className="w-full h-full object-cover"
          onError={handleImageError}
          loading="lazy"
        />

        {/* Heart Button - Desktop only */}
        {showFavoriteButton && (
          <button
            onClick={handleFavorite}
            className="hidden md:block absolute top-3 right-3 p-1.5 transition-all duration-300 hover:scale-110 active:scale-95 z-10"
          >
            <Heart 
              size={16} 
              className={`${
                isFavorited 
                  ? 'text-red-500 fill-red-500' 
                  : 'text-white/70 hover:text-red-500'
              } transition-all duration-300`} 
            />
          </button>
        )}

      </div>

      {/* Piece Info */}
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        {/* Title */}
        <h3 className="text-white font-bold text-sm line-clamp-1 group-hover:text-gray-200 transition-colors">
          {item.name}
        </h3>

        {/* Badge Area - Fixed height to ensure consistency */}
        <div className="min-h-[24px]">
          {/* Group Info Badge (Frontend Grouping) */}
          {showEditionBadges && isGrouped && groupLabel && (
            <div className="flex items-center gap-1.5 text-xs">
              <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-gray-300/10 to-gray-400/10 border border-gray-300/40 rounded-md text-gray-300 shadow-sm">
                <Layers size={12} />
                <span>{groupLabel}</span>
              </div>
            </div>
          )}

          {/* Edition Info Badge (Database Editions - legacy) */}
          {showEditionBadges && !isGrouped && editionInfo.isEdition && (
            <div className="flex items-center gap-1.5 text-xs">
              <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-gray-300/10 to-gray-400/10 border border-gray-300/40 rounded-md text-gray-300 shadow-sm">
                <Layers size={12} />
                <span>{editionInfo.label}</span>
              </div>
              {!editionInfo.isSoldOut && editionInfo.availableCount > 0 && (
                <div className="px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400">
                  {editionInfo.availableCount} left
                </div>
              )}
              {editionInfo.isSoldOut && (
                <div className="px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded-md text-red-400">
                  Sold Out
                </div>
              )}
            </div>
          )}
        </div>

        {/* Price */}
        <div className="space-y-0.5">
          <div className="text-green-400 font-semibold text-sm">
            {formatPrice(displayPrice)}
          </div>
          <div className="text-gray-500 text-xs">
            ≈ ${(parseFloat(displayPrice || 0) * solPrice).toFixed(2)} USD
          </div>
        </div>

        {/* Additional info for capsules */}
        {item.type === 'capsule' && item.pieceCount && (
          <div className="text-xs text-gray-400">
            {item.pieceCount} {item.pieceCount === 1 ? 'piece' : 'pieces'}
          </div>
        )}

        {/* Created date */}
        {item.createdDate && (
          <div className="text-xs text-gray-500">
            {item.status === 'draft' ? 'Created' : 'Dropped'} {new Date(item.createdDate).toLocaleDateString('en-US', { 
              year: '2-digit', 
              month: '2-digit', 
              day: '2-digit' 
            })}
          </div>
        )}
      </div>

      {/* Hover Action Buttons - Slides up from bottom */}
      <div className="absolute bottom-0 left-0 right-0 transform translate-y-full group-hover:translate-y-0 transition-all duration-300 ease-out p-3">
        <div className="flex gap-2">
          {showBuyButton && (
            <button
              onClick={handleBuy}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-3 rounded-lg font-semibold transition-all duration-200 text-sm shadow-lg hover:shadow-blue-500/25"
            >
              <span>{buyButtonText}</span>
            </button>
          )}
          {showTradeButton && (
            <button
              onClick={handleTrade}
              className="flex-1 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white py-2.5 px-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center text-sm shadow-lg border border-[#3a3a3a]"
            >
              <ArrowLeftRight size={14} className="mr-1" />
              {tradeButtonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedPieceCard;
